/**
 * Where a Sabine chat about each kind of record is filed, keyed by the record
 * kind: a shared thread's contextType, and the `recordKind` a coach page sends
 * with each turn.
 *
 * - `session` is the SabineChatSession source its saved thread lives under.
 *   The share seeds a copy from that saved thread (lib/ai-chat-subject.js) and
 *   the chat library files the copy beside it (lib/chat-library/service.js);
 *   both read this one table, so they cannot disagree on where a thread lives.
 *   A readout's thread stays under RESULT_COACH, where saved ones already are.
 * - `feedback` is the AIChatFeedback source each turn is logged under, one per
 *   surface, so a quality report on one surface reads only its own turns.
 * - `surface` is the Sabine surface (lib/sabine/surfaces.js) whose coach runs
 *   on the record, and what its saved thread records it was saved from.
 */
const SOURCES_BY_RECORD_KIND = {
  SIMULATION_RESULT: { session: "RESULT_COACH", feedback: "RESULT_COACH", surface: "result" },
  COG_REPORT: { session: "RESULT_COACH", feedback: "DEAL_CLINIC", surface: "readout" },
  TRANSCRIPT_RESULT: { session: "TRANSCRIPT_COACH", feedback: "TRANSCRIPT_COACH", surface: "transcript" },
  PLAYBOOK: { session: "PLAYBOOK", feedback: "PLAYBOOK", surface: "playbook" },
  REPORTS_CHAT: { session: "REPORTS", feedback: "REPORTS", surface: "reports" },
};

const SESSION_SOURCES = new Set([
  "GENERIC", "HOME_SABINE", "EMAIL", "DEAL_CLINIC",
  ...Object.values(SOURCES_BY_RECORD_KIND).map(({ session }) => session),
]);

/** Whether a source names a supported saved conversation, including legacy generic threads. */
export function isSabineSessionSource(source) {
  return SESSION_SOURCES.has(source);
}

// The scenario type a coach page sent before it sent `recordKind`.
const RECORD_KIND_ALIASES = {
  DEAL_DIAGNOSIS: "COG_REPORT",
  TRANSCRIPT: "TRANSCRIPT_RESULT",
};

/** The record kind any spelling names: "DEAL_DIAGNOSIS" is "COG_REPORT". */
export function normalizeRecordKind(kind) {
  const value = String(kind || "").toUpperCase();
  return RECORD_KIND_ALIASES[value] || value;
}

/** @returns {{ session: string, feedback: string, surface: string }|null} null for any other kind */
export function getSabineSources(kind) {
  return SOURCES_BY_RECORD_KIND[normalizeRecordKind(kind)] || null;
}

/** @returns {string|null} null for a kind no chat is saved under */
export function getSabineSessionSource(kind) {
  return getSabineSources(kind)?.session || null;
}

/** @returns {string|null} the surface that coaches on the kind; null for any other */
export function getSabineSurfaceName(kind) {
  return getSabineSources(kind)?.surface || null;
}
